import { useApi } from "@/hooks/use-api";
import { useServerEvents } from "@/hooks/use-server-events";
import { useAuth } from "@/hooks/use-auth";
import { Routes } from "@/Routes";
import { useAppDispatch, useAppState } from "@/store/useApp";
import * as Frigade from "@frigade/react";
import { ErrorBoundary } from "@sentry/react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast, Toaster } from "sonner";
import { config } from "./config";
import { useAnalytics } from "./hooks/use-analytics";
import Loading from "./features/shell/Loading";
import { buildSigninUrl } from "./auth/auth";
import { isAuthenticationError } from "./auth/AuthError";

const App = () => {
  const dispatch = useAppDispatch();
  const { fetchMe, fetchDocuments, fetchAssessments } = useApi();
  const { isAuthenticated, isLoading } = useAuth();
  const { identifyUser } = useAnalytics();
  const [isLoadingUserData, setIsLoadingUserData] = useState<boolean>(false);
  const assessments = useAppState((state) => state.assessments);
  const appError = useAppState((state) => state.error);
  const appUser = useAppState((state) => state.user);
  const location = useLocation();
  const navigate = useNavigate();
  const isLtiCallbackRoute = location.pathname.startsWith("/lti/callback");
  const isAuthEntryRoute =
    location.pathname.startsWith("/signin") ||
    location.pathname.startsWith("/lti/");

  useServerEvents({
    enabled: isAuthenticated && !isAuthEntryRoute,
    onRefreshDocuments: fetchDocuments,
    onRefreshAssessments: fetchAssessments,
  });

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated && !isAuthEntryRoute) {
      navigate(buildSigninUrl(), { replace: true });
      return;
    }

    if (isAuthenticated && location.pathname.startsWith("/signin")) {
      navigate("/", { replace: true });
    }
  }, [
    isAuthenticated,
    isLoading,
    isAuthEntryRoute,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (appError) {
        return;
      }

      if (
        isAuthenticated &&
        !isLoadingUserData &&
        assessments === null &&
        !isLtiCallbackRoute
      ) {
        setIsLoadingUserData(true);
        try {
          await fetchMe();
        } catch (error) {
          if (isAuthenticationError(error)) {
            return;
          }

          dispatch({
            type: "SET_ERROR",
            payload: {
              type: "network",
              message:
                error instanceof Error ? error.message : "Failed to load user data",
              timestamp: Date.now(),
            },
          });
          toast.error("Unable to connect to server. Please try again later.");
        } finally {
          setIsLoadingUserData(false);
        }
      }
    };

    if (!isLoading && isAuthenticated && !appError) {
      void loadInitialData();
    }
  }, [
    fetchMe,
    isAuthenticated,
    isLoading,
    isLoadingUserData,
    assessments,
    appError,
    dispatch,
    isLtiCallbackRoute,
  ]);

  useEffect(() => {
    if (!appUser) {
      return;
    }

    void identifyUser({
      id: appUser.id,
      email: appUser.email,
      name: appUser.name,
      nickname: appUser.nickname,
    });
  }, [appUser, identifyUser]);

  if (isLoading || (isAuthenticated && isLoadingUserData && !isLtiCallbackRoute)) {
    return <Loading />;
  }

  if (!isAuthenticated && !isAuthEntryRoute) {
    return <Loading />;
  }

  return (
    <>
      <Frigade.Provider
        apiKey={config.frigade.apiKey}
        userId={appUser?.id}
        theme={{
          colors: {
            primary: {
              background: "#ffffff",
              border: "#FF5531",
              surface: "#FF5531",
              hover: {
                background: "#ffffff",
                surface: "#E64422", // Darker version of #FF5531
                border: "#E64422", // Darker version of #FF5531
              },
              active: {
                background: "#ffffff",
                surface: "#E64422", // Darker version of #FF5531
                border: "#E64422", // Darker version of #FF5531
              },
            },
          },
        }}
      >
        <ErrorBoundary
          fallback={({ error, resetError }) => {
            console.error("🚨 Error boundary caught error:", error);
            toast.error("Something went wrong.");
            return (
              <div className="flex flex-col items-center justify-center h-screen bg-[#FFFEFD] p-8">
                <h1 className="text-2xl font-bold text-red-600 mb-4">
                  Something went wrong
                </h1>
                <p className="text-gray-600 mb-4 text-center max-w-md">
                  {error instanceof Error
                    ? error.message
                    : "An unexpected error occurred"}
                </p>
                <button
                  onClick={resetError}
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
                >
                  Try again
                </button>
                <details className="mt-4 text-xs text-gray-500">
                  <summary>Error details</summary>
                  <pre className="mt-2 whitespace-pre-wrap">
                    {error instanceof Error ? error.stack : String(error)}
                  </pre>
                </details>
              </div>
            );
          }}
          showDialog
        >
          <Routes />
        </ErrorBoundary>
      </Frigade.Provider>

      <Toaster />
    </>
  );
};

export default App;
